import React, { useState } from 'react';
// Constants, helpers, and sub-components for CommercialsEditor

export const LEVELS = ['Junior', 'Mid', 'Senior', 'Lead', 'Architect', 'Manager'];

export const CURRENCIES = [
    { code: 'AED', symbol: 'AED', label: 'AED' },
    { code: 'IND', symbol: '₹', label: 'IND (₹)' },
    { code: 'USD', symbol: '$', label: 'USD ($)' },
    { code: 'SAR', symbol: 'SAR', label: 'SAR' },
    { code: 'EUR', symbol: '€', label: 'EUR (€)' },
];

export const PRICING_MODELS = ['Fixed Price', 'Time & Material', 'Hybrid'];

export const newResId = (() => { let i = 0; return () => ++i; })();

export const blankResource = () => ({
    _id: newResId(),
    title: '',
    level: 'Mid',
    cost_per_month: '',
    months: '',
    count: 1,
    total_cost: 0,
});

export const blankAddlCost = () => ({
    _id: newResId(),
    label: '',
    count: 1,
    cost_per_item: '',
    amount: 0,
});

// ── Formatting helpers ────────────────────────────────────────────────────────

/** Format a number with commas (en-US), e.g. 500000 → "500,000" */
const commaFmt = (n) =>
    new Intl.NumberFormat('en-US').format(Math.round(n));

/**
 * Display amount with currency code prefix.
 * e.g. fmt(500000, 'AED') → "AED 500,000"
 *      fmt(500000, '₹')   → "₹ 500,000"
 */
export const fmt = (n, sym) => {
    if (!n && n !== 0) return '—';
    const rounded = Math.round(n);
    const formatted = new Intl.NumberFormat('en-US').format(rounded);
    return `${sym} ${formatted}`;
};

export const calcSummary = (resources = [], additionalCosts = [], params = {}, boq = []) => {
    const {
        overhead_pct = 12,
        buffer_pct = 10,
        profit_margin_pct = 20,
        tax_pct = 5,
        discount_pct = 0,
    } = params;
    const resTotal = resources.reduce(
        (acc, r) => acc + (parseFloat(r.cost_per_month) || 0) * (parseFloat(r.months) || 0) * (parseInt(r.count, 10) || 1),
        0
    );
    // For additional costs: support both legacy `amount` and new `count * cost_per_item`
    const addlTotal = additionalCosts.reduce((acc, c) => {
        if (c.cost_per_item !== undefined) {
            return acc + ((parseFloat(c.cost_per_item) || 0) * (parseInt(c.count, 10) || 1));
        }
        return acc + (parseFloat(c.amount) || 0);
    }, 0);
    const boqTotal = boq.reduce((acc, b) => {
        const val = parseFloat(b.estimated_amount);
        return acc + (isNaN(val) ? 0 : val);
    }, 0);
    const base = resTotal + addlTotal + boqTotal;
    const ohAmt = base * (overhead_pct / 100);
    const bufAmt = (base + ohAmt) * (buffer_pct / 100);
    const costTotal = base + ohAmt + bufAmt;
    const mgAmt = costTotal * (profit_margin_pct / 100);
    const preSell = costTotal + mgAmt;
    const discAmt = preSell * (discount_pct / 100);
    const sellPrice = preSell - discAmt;
    const vatAmt = sellPrice * (tax_pct / 100);
    const grandTotal = sellPrice + vatAmt;
    const totalHeadcount = resources.reduce((acc, r) => acc + (parseInt(r.count, 10) || 1), 0);
    return {
        base_resource_cost: resTotal,
        total_additional_cost: addlTotal,
        total_boq_cost: boqTotal,
        total_overhead: ohAmt,
        total_buffer: bufAmt,
        total_cost: costTotal,
        profit_margin_amt: mgAmt,
        discount_amount: discAmt,
        selling_price_pre_tax: sellPrice,
        tax_amount: vatAmt,
        grand_total: grandTotal,
        total_headcount: totalHeadcount,
    };
};

// ─── Shared input style ───────────────────────────────────────────────────────

const cellInputStyle = {
    width: '100%',
    height: '36px',
    padding: '0 10px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#1E293B',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.18s, box-shadow 0.18s',
    fontFamily: 'inherit',
};

const numInputStyle = { ...cellInputStyle, textAlign: 'right', paddingRight: '10px' };

const selectStyle = {
    ...cellInputStyle,
    cursor: 'pointer',
    paddingRight: '8px',
};

// Input with focus ring via event handlers
const InlineInput = ({ style = {}, ...props }) => {
    const [focused, setFocused] = useState(false);
    return (
        <input
            {...props}
            style={{
                ...(props.type === 'number' ? numInputStyle : cellInputStyle),
                ...(focused ? { borderColor: '#2276E9', boxShadow: '0 0 0 3px rgba(249,115,22,0.12)', background: '#FFFFFF' } : {}),
                ...style
            }}
            onFocus={(e) => { setFocused(true); props.onFocus && props.onFocus(e); }}
            onBlur={(e) => { setFocused(false); props.onBlur && props.onBlur(e); }}
        />
    );
};

const InlineSelect = ({ style = {}, ...props }) => {
    const [focused, setFocused] = useState(false);
    return (
        <select
            {...props}
            style={{
                ...selectStyle,
                ...(focused ? { borderColor: '#2276E9', boxShadow: '0 0 0 3px rgba(249,115,22,0.12)', background: '#FFFFFF' } : {}),
                ...style
            }}
            onFocus={(e) => { setFocused(true); props.onFocus && props.onFocus(e); }}
            onBlur={(e) => { setFocused(false); props.onBlur && props.onBlur(e); }}
        />
    );
};

// ─── Sub-Components ───────────────────────────────────────────────────────────

export const ResourceRow = ({ res, idx, sym, onUpdate, onDelete }) => {
    const rowCost =
        (parseFloat(res.cost_per_month) || 0) *
        (parseFloat(res.months) || 0) *
        (parseInt(res.count, 10) || 1);

    return (
        <tr className="ce-tr-hover">
            {/* SL NO. */}
            <td className="td-sl">{idx + 1}</td>

            {/* Resource Type */}
            <td className="td-pad">
                <InlineInput
                    type="text"
                    placeholder="e.g. Backend Developer"
                    value={res.title}
                    onChange={(e) => onUpdate(res._id, 'title', e.target.value)}
                />
            </td>

            {/* Cost / Month */}
            <td className="td-pad">
                <InlineInput
                    type="number"
                    placeholder="0"
                    value={res.cost_per_month}
                    min="0"
                    step="500"
                    onChange={(e) => onUpdate(res._id, 'cost_per_month', e.target.value)}
                />
            </td>

            {/* Months */}
            <td className="td-pad">
                <InlineInput
                    type="number"
                    placeholder="0"
                    value={res.months}
                    min="0"
                    step="1"
                    onChange={(e) => onUpdate(res._id, 'months', e.target.value)}
                    style={{ textAlign: 'center' }}
                />
            </td>

            {/* Count */}
            <td className="td-pad">
                <InlineInput
                    type="number"
                    placeholder="1"
                    value={res.count}
                    min="1"
                    step="1"
                    onChange={(e) => onUpdate(res._id, 'count', e.target.value)}
                    style={{ textAlign: 'center' }}
                />
            </td>

            {/* Level */}
            <td className="td-pad">
                <InlineSelect
                    value={res.level}
                    onChange={(e) => onUpdate(res._id, 'level', e.target.value)}
                    style={{ textAlign: 'center' }}
                >
                    {LEVELS.map((l) => <option key={l}>{l}</option>)}
                </InlineSelect>
            </td>

            {/* Total Cost */}
            <td className="td-total">
                {rowCost > 0
                    ? <span className="td-total-val">{fmt(rowCost, sym)}</span>
                    : <span className="td-total-empty">—</span>
                }
            </td>

            {/* Delete */}
            <td className="td-action">
                <button className="ce-del-btn" onClick={() => onDelete(res._id)} title="Remove row">✕</button>
            </td>
        </tr>
    );
};

export const AddlCostRow = ({ item, sym, onUpdate, onDelete }) => {
    const totalCost =
        (parseFloat(item.cost_per_item) || 0) * (parseInt(item.count, 10) || 1);

    // Support legacy `amount` field
    const displayTotal = item.cost_per_item !== undefined
        ? totalCost
        : (parseFloat(item.amount) || 0);

    return (
        <div className="ce-addl-row-grid">
            {/* Particular */}
            <InlineInput
                type="text"
                placeholder="e.g. Software License"
                value={item.label}
                onChange={(e) => onUpdate(item._id, 'label', e.target.value)}
            />

            {/* Count */}
            <InlineInput
                type="number"
                placeholder="1"
                min="1"
                value={item.count ?? 1}
                onChange={(e) => {
                    onUpdate(item._id, 'count', e.target.value);
                    const newTotal = (parseFloat(item.cost_per_item) || 0) * (parseInt(e.target.value, 10) || 1);
                    onUpdate(item._id, 'amount', newTotal);
                }}
                style={{ textAlign: 'center' }}
            />

            {/* Cost Per Item */}
            <InlineInput
                type="number"
                placeholder="0"
                min="0"
                value={item.cost_per_item ?? item.amount ?? ''}
                onChange={(e) => {
                    onUpdate(item._id, 'cost_per_item', e.target.value);
                    const newTotal = (parseFloat(e.target.value) || 0) * (parseInt(item.count, 10) || 1);
                    onUpdate(item._id, 'amount', newTotal);
                }}
                style={{ textAlign: 'right' }}
            />

            {/* Total Cost */}
            <span className="addl-total-val">
                {displayTotal > 0 ? fmt(displayTotal, sym) : <span className="addl-total-empty">—</span>}
            </span>

            {/* Delete */}
            <button className="ce-del-btn" onClick={() => onDelete(item._id)} title="Remove">✕</button>
        </div>
    );
};

export const blankBOQItem = () => ({
    _id: newResId(),
    sl_no: '',
    item_name: '',
    description: '',
    model: '',
    model_specs: '',
    quantity: '',
    unit: '',
    amount: '',
    estimated_amount: '',
    total_amount: '',
});

export const BOQRow = ({ item, idx, sym, onUpdate, onDelete }) => {
    return (
        <tr className="ce-tr-hover">
            {/* SL NO. */}
            <td className="td-sl" style={{ textAlign: 'center' }}>
                <InlineInput
                    type="text"
                    value={item.sl_no}
                    onChange={(e) => onUpdate(item._id, 'sl_no', e.target.value)}
                    style={{ textAlign: 'center', width: '50px', margin: '0 auto' }}
                />
            </td>

            {/* Item Name */}
            <td className="td-pad">
                <InlineInput
                    type="text"
                    placeholder="Item Name"
                    value={item.item_name || ''}
                    onChange={(e) => {
                        onUpdate(item._id, 'item_name', e.target.value);
                    }}
                />
            </td>

            {/* Description */}
            <td className="td-pad">
                <InlineInput
                    type="text"
                    placeholder="Description"
                    value={item.description || ''}
                    onChange={(e) => {
                        onUpdate(item._id, 'description', e.target.value);
                    }}
                />
            </td>

            {/* Model */}
            <td className="td-pad">
                <InlineInput
                    type="text"
                    placeholder="Model"
                    value={item.model || ''}
                    onChange={(e) => {
                        onUpdate(item._id, 'model', e.target.value);
                        onUpdate(item._id, 'model_specs', e.target.value);
                    }}
                />
            </td>

            {/* Quantity */}
            <td className="td-pad" style={{ width: '80px' }}>
                <InlineInput
                    type="text"
                    placeholder="0"
                    value={item.quantity || ''}
                    onChange={(e) => onUpdate(item._id, 'quantity', e.target.value)}
                    style={{ textAlign: 'center' }}
                />
            </td>

            {/* Unit */}
            <td className="td-pad" style={{ width: '80px' }}>
                <InlineInput
                    type="text"
                    placeholder="e.g. Nos"
                    value={item.unit || ''}
                    onChange={(e) => onUpdate(item._id, 'unit', e.target.value)}
                    style={{ textAlign: 'center' }}
                />
            </td>

            {/* Amount */}
            <td className="td-pad" style={{ width: '110px' }}>
                <InlineInput
                    type="text"
                    placeholder="Not specified"
                    value={item.amount || ''}
                    onChange={(e) => {
                        onUpdate(item._id, 'amount', e.target.value);
                        onUpdate(item._id, 'estimated_amount', e.target.value);
                    }}
                    style={{ textAlign: 'right' }}
                />
            </td>

            {/* Total Amount */}
            <td className="td-pad" style={{ width: '110px' }}>
                <InlineInput
                    type="text"
                    placeholder="Not specified"
                    value={item.total_amount || ''}
                    onChange={(e) => {
                        onUpdate(item._id, 'total_amount', e.target.value);
                    }}
                    style={{ textAlign: 'right' }}
                />
            </td>

            {/* Actions */}
            <td className="td-action">
                <button className="ce-del-btn" onClick={() => onDelete(item._id)} title="Remove row">✕</button>
            </td>
        </tr>
    );
};

